import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { PortProtocol, portProtocolLabel } from '@/consts/machine'
import { useUnifiedContext } from '@/contexts/UnifiedContext'
import { Instance } from '@/models/instance'
import { formatDatePreset } from '@/utils/formatter'

interface InstanceConfigProps {
  instance: Instance
}

export function InstanceConfig({ instance }: InstanceConfigProps) {
  const { t } = useTranslation()
  const context = useUnifiedContext()

  const snapshotInfos = useMemo(() => {
    if (!instance.fromSnapshot) return []

    const { createTime, subject } = instance.fromSnapshot

    const result = [
      {
        name: 'Snapshot',
        value: subject,
      },
    ]
    const createTimeStr = formatDatePreset(createTime.seconds * 1000, 'minute')

    result.push({
      name: t('instance.detail.config.snapshot'),
      value: `Created at：${createTimeStr}`,
    })

    return result
  }, [instance])

  const imageInfos = useMemo(() => {
    const { image } = instance.rentInfos

    const result = [
      {
        name: t('instance.detail.config.image'),
        value: image.alias,
      },
    ]

    if (image.description) {
      result.push({
        name: t('instance.detail.config.imageDesc'),
        value: image.description,
      })
    }

    return result
  }, [instance])

  const rentInfos = useMemo(() => {
    const { cmd, ports } = instance.rentInfos

    const result = []

    if (cmd) {
      result.push({
        name: t('instance.detail.config.cmd'),
        key: 'cmd',
        value: cmd,
      })
    }

    if (ports.length > 0) {
      const protsVal = ports
        .map((p) => `${portProtocolLabel[p.protocol as PortProtocol] || 'unknown'}/${p.srcPort}`)
        .join(', ')

      result.push({
        name: t('instance.detail.config.port'),
        key: 'ports',
        value: protsVal,
      })
    }

    return result
  }, [instance])

  // Content panel (shared between platform and team)
  const content = (
    <div className="bg-panel-gray-100 rounded-lg flex flex-wrap gap-y-4 py-4 t-xs text-tertiary">
      {imageInfos.map((img, idx) => (
        <div className="w-1/2 px-4" key={`img-${idx}`}>
          <div className="t-xs text-secondary">{img.name}</div>
          <div className="t-sb text-primary">{img.value}</div>
        </div>
      ))}

      {snapshotInfos.map((snap, idx) => (
        <div className="w-1/2 px-4" key={`snap-${idx}`}>
          <div className="t-xs text-secondary">{snap.name}</div>
          <div className="t-sb text-primary">{snap.value}</div>
        </div>
      ))}

      {rentInfos.map((info, idx) => (
        <div className="w-1/2 px-4" key={`info-${idx}`}>
          <div className="t-xs text-secondary">{info.name}</div>
          <div className="t-sb text-primary">{info.value}</div>
        </div>
      ))}
    </div>
  )

  // Platform mode: add wrapper div
  // Team mode: return content directly (no wrapper)
  if (context.mode === 'platform') {
    return <div className="flex flex-col gap-4">{content}</div>
  }

  return content
}
